import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  getSourceDictValues,
  saveProdDictMapping,
  getProdDictMapping,
  getProdDictItems,
  type ProdColumnBinding,
  type ProdDictMappingEntry,
  type ProdDictItem,
} from '../../services/data-integration-api';

interface DictFieldConfig {
  sourceColumnName: string;
  prodColumnName: string;
}

interface ProdDictMappingPanelProps {
  normalizedTableId: string;
  writeMappingId: string;
  prodConnectionId?: string;
  dictFields: DictFieldConfig[];
  onComplete: (mappingStatus: string, dictConfigStatus: string) => void;
}

interface DictRow {
  source_dict_value: string;
  count: number;
  prod_dict_value: string;
  prod_dict_label: string;
  mapping_status: 'mapped' | 'unmapped' | 'skipped';
}

export default function ProdDictMappingPanel({
  normalizedTableId, writeMappingId, prodConnectionId, dictFields, onComplete,
}: ProdDictMappingPanelProps) {
  const [activeField, setActiveField] = useState(0);
  const [dictRows, setDictRows] = useState<DictRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldStatus, setFieldStatus] = useState<Record<string, boolean>>({});
  // 生产库字典项下拉选项（从统一字典表获取）
  const [prodDictOptions, setProdDictOptions] = useState<ProdDictItem[]>([]);
  const [dictTypeInput, setDictTypeInput] = useState('');
  const [loadingDictItems, setLoadingDictItems] = useState(false);

  const currentField = dictFields[activeField];

  // 加载当前字段的字典值
  const loadDictValues = useCallback(async () => {
    if (!currentField) return;
    setLoading(true);
    try {
      // 获取源表字典值
      const sourceResult = await getSourceDictValues({
        normalizedTableId,
        columnName: currentField.sourceColumnName,
      });

      // 获取已保存的对照
      const existingResult = await getProdDictMapping({
        writeMappingId,
        sourceColumnName: currentField.sourceColumnName,
      });

      const existingMap = new Map(
        existingResult.items.map(d => [d.source_dict_value, d])
      );

      const rows: DictRow[] = sourceResult.values.map(v => {
        const existing = existingMap.get(v.value);
        return {
          source_dict_value: v.value,
          count: v.count,
          prod_dict_value: existing?.prod_dict_value || '',
          prod_dict_label: existing?.prod_dict_label || '',
          mapping_status: existing?.mapping_status as DictRow['mapping_status'] || 'unmapped',
        };
      });

      setDictRows(rows);
    } catch (e: any) {
      toast.error('加载字典值失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [currentField, normalizedTableId, writeMappingId]);

  useEffect(() => {
    loadDictValues();
    // 切换字段时重置字典项
    setProdDictOptions([]);
    setDictTypeInput('');
  }, [loadDictValues]);

  // 加载生产库字典项
  const handleLoadProdDictItems = useCallback(async () => {
    if (!prodConnectionId || !dictTypeInput.trim()) {
      toast.error('请输入字典类型编码');
      return;
    }
    setLoadingDictItems(true);
    try {
      const result = await getProdDictItems({
        prodConnectionId,
        dictType: dictTypeInput.trim(),
      });
      setProdDictOptions(result.items);
      if (result.items.length === 0) {
        toast.info('该字典类型无可用项');
      } else {
        toast.success(`加载 ${result.items.length} 个字典项`);
      }
    } catch (e: any) {
      toast.error('加载字典项失败: ' + e.message);
    } finally {
      setLoadingDictItems(false);
    }
  }, [prodConnectionId, dictTypeInput]);

  // 更新行
  const updateRow = useCallback((index: number, updates: Partial<DictRow>) => {
    setDictRows(prev => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      if (updates.prod_dict_value !== undefined) {
        next[index].mapping_status = updates.prod_dict_value ? 'mapped' : 'unmapped';
      }
      return next;
    });
  }, []);

  // 保存当前字段的对照
  const handleSave = useCallback(async () => {
    if (!currentField) return;
    setSaving(true);
    try {
      const dictMappings: ProdDictMappingEntry[] = dictRows.map(r => ({
        source_dict_value: r.source_dict_value,
        prod_dict_value: r.prod_dict_value,
        prod_dict_label: r.prod_dict_label,
        mapping_status: r.mapping_status,
      }));

      const result = await saveProdDictMapping({
        writeMappingId,
        sourceColumnName: currentField.sourceColumnName,
        prodColumnName: currentField.prodColumnName,
        dictMappings,
      });

      setFieldStatus(prev => ({ ...prev, [currentField.sourceColumnName]: true }));
      toast.success(`保存成功: ${result.saved_count} 条对照`);

      onComplete(result.mapping_status, result.dict_config_status);

      // 自动切换到下一个未配置的字段
      if (activeField < dictFields.length - 1) {
        setActiveField(activeField + 1);
      }
    } catch (e: any) {
      toast.error('保存失败: ' + e.message);
    } finally {
      setSaving(false);
    }
  }, [currentField, dictRows, writeMappingId, onComplete, activeField, dictFields.length]);

  if (dictFields.length === 0) {
    return <p className="text-muted-foreground">无字典类字段需要配置</p>;
  }

  const mappedCount = dictRows.filter(r => r.mapping_status === 'mapped').length;

  return (
    <div>
      <h4 className="text-sm font-medium mb-3">字典对照配置 ({dictFields.length} 个字典字段)</h4>

      {/* 字段选择 tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {dictFields.map((f, i) => (
          <Button
            key={f.sourceColumnName}
            variant={i === activeField ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveField(i)}
          >
            {f.sourceColumnName}
            {fieldStatus[f.sourceColumnName] && <Badge variant="default" className="ml-1 text-[10px]">已配置</Badge>}
          </Button>
        ))}
      </div>

      {currentField && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">
              源字段: <span className="font-mono">{currentField.sourceColumnName}</span> → 生产库字段: <span className="font-mono">{currentField.prodColumnName}</span>
            </span>
            <Badge variant="secondary">{mappedCount}/{dictRows.length} 已映射</Badge>
          </div>

          {loading ? (
            <p className="text-muted-foreground">加载中...</p>
          ) : (
            <>
              {/* 生产库字典类型加载 */}
              {prodConnectionId && (
                <div className="flex items-center gap-2 mb-3 p-3 bg-muted/30 rounded">
                  <label className="text-xs font-medium whitespace-nowrap">字典类型:</label>
                  <Input
                    value={dictTypeInput}
                    onChange={e => setDictTypeInput(e.target.value)}
                    placeholder="输入 dict_type（如 sys_gender）"
                    className="h-8 text-xs max-w-64"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLoadProdDictItems}
                    disabled={loadingDictItems}
                  >
                    {loadingDictItems ? '加载中...' : '加载字典项'}
                  </Button>
                  {prodDictOptions.length > 0 && (
                    <Badge variant="secondary" className="text-[10px]">{prodDictOptions.length} 项</Badge>
                  )}
                </div>
              )}

              <div className="border rounded-md overflow-auto max-h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">源字典值</TableHead>
                      <TableHead className="w-[80px]">记录数</TableHead>
                      <TableHead className="w-[200px]">生产库字典值</TableHead>
                      <TableHead className="w-[200px]">生产库标签</TableHead>
                      <TableHead className="w-[80px]">状态</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dictRows.map((r, i) => (
                      <TableRow key={r.source_dict_value} className={r.mapping_status === 'unmapped' ? 'bg-yellow-50' : ''}>
                        <TableCell className="font-mono text-xs">{r.source_dict_value || '(空值)'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{r.count}</TableCell>
                        <TableCell>
                          {prodDictOptions.length > 0 ? (
                            <select
                              value={r.prod_dict_value}
                              onChange={e => {
                                const selected = prodDictOptions.find(o => o.dict_code === e.target.value);
                                updateRow(i, {
                                  prod_dict_value: e.target.value,
                                  prod_dict_label: selected?.dict_label || '',
                                });
                              }}
                              className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                            >
                              <option value="">-- 选择 --</option>
                              {prodDictOptions.map(o => (
                                <option key={o.dict_code} value={o.dict_code}>
                                  {o.dict_code} - {o.dict_label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <Input
                              value={r.prod_dict_value}
                              onChange={e => updateRow(i, { prod_dict_value: e.target.value })}
                              placeholder="输入生产库字典值"
                              className="h-8 text-xs"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {prodDictOptions.length > 0 ? (
                            <span className="text-xs text-muted-foreground">{r.prod_dict_label || '-'}</span>
                          ) : (
                            <Input
                              value={r.prod_dict_label}
                              onChange={e => updateRow(i, { prod_dict_label: e.target.value })}
                              placeholder="标签（可选）"
                              className="h-8 text-xs"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={r.mapping_status === 'mapped' ? 'default' : 'secondary'} className="text-[10px]">
                            {r.mapping_status === 'mapped' ? '已映射' : '未映射'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex gap-2 mt-4">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? '保存中...' : '保存对照'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
